import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Progress,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle, FiMessageSquare, FiDatabase, FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { documentsApi, collectionsApi, DocumentPreview as DocumentPreviewType, BatchStatus, ProcessingResults } from '../services/api';
import DocumentPreview from '../components/DocumentPreview';
import PostProcessingResults from '../components/PostProcessingResults';

const DocumentUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [collectionName, setCollectionName] = useState('');
  const [basePickerOpen, setBasePickerOpen] = useState(false); // dropdown de busca de base
  const [processingDocuments, setProcessingDocuments] = useState<string[]>([]);
  const [batchProcessing, setBatchProcessing] = useState<{
    batch_id: string;
    total: number;
    completed: number;
    failed: number;
  } | null>(null);
  
  // Preview states (unificado para arquivo único e lote)
  const [documentPreview, setDocumentPreview] = useState<DocumentPreviewType | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Processing results state
  const [processingResults, setProcessingResults] = useState<any | null>(null);
  const [showResults, setShowResults] = useState(false);
  
  // Batch results state
  const [batchResults, setBatchResults] = useState<BatchStatus | null>(null);
  const [showBatchResults, setShowBatchResults] = useState(false);
  
  // Real processing results from Qdrant/Neo4j
  const [realResults, setRealResults] = useState<ProcessingResults | null>(null);
  const [showRealResults, setShowRealResults] = useState(false);
  const [isLoadingRealResults, setIsLoadingRealResults] = useState(false);
  
  const toast = useToast();
  const queryClient = useQueryClient();

  // Fetch collections for dropdown
  const { data: collections = [] } = useQuery(
    'collections',
    collectionsApi.list,
    { 
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
    }
  );

  // Fetch supported formats
  const { data: supportedFormats } = useQuery(
    'supported-formats',
    documentsApi.getSupportedFormats
  );

  // Preview mutation - removido, agora usa ProcessingProgress para todos os tipos

  // Upload mutation
  const uploadMutation = useMutation(
    ({ file, collection }: { file: File; collection: string }) => {
      console.log('🚀 [UPLOAD] Iniciando upload:', {
        filename: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        collection: collection,
        timestamp: new Date().toISOString()
      });
      return documentsApi.upload(file, collection);
    },
    {
      onSuccess: (data) => {
        console.log('✅ [UPLOAD] Upload bem-sucedido:', {
          documentId: data.id,
          filename: data.filename,
          status: data.status,
          collection: data.collection_name,
          timestamp: new Date().toISOString()
        });
        
        toast({
          title: 'Processamento iniciado',
          description: `${data.filename} está sendo processado e indexado para busca`,
          status: 'success',
          duration: 5000,
        });
        setProcessingDocuments(prev => [...prev, data.id]);
        setSelectedFiles([]);
        // Mantém a base selecionada — o usuário quase sempre envia vários
        // documentos para a MESMA base. (Antes limpava e travava o próximo upload.)
        setShowPreview(false);
        setDocumentPreview(null);
        setShowRealResults(false);
        setRealResults(null);

        // Start polling for status
        console.log('🔄 [UPLOAD] Iniciando polling para documento:', data.id);
        pollDocumentStatus(data.id);
      },
      onError: (error: any) => {
        console.error('❌ [UPLOAD] Erro no upload:', {
          error: error.response?.data?.detail || error.message,
          status: error.response?.status,
          timestamp: new Date().toISOString()
        });
        
        const detail = error.response?.data?.detail;
        const code = error.response?.status;
        const isLimit = [402, 409, 413, 429].includes(code);
        toast({
          title: isLimit ? 'Limite do seu plano' : 'Não foi possível enviar o documento',
          description: detail || 'Tente novamente em instantes. Se continuar, fale com o suporte.',
          status: isLimit ? 'warning' : 'error',
          duration: 6000,
          isClosable: true,
        });
      },
    }
  );

  // Batch upload mutation
  const batchUploadMutation = useMutation(
    ({ files, collection }: { files: File[]; collection: string }) => {
      console.log('🚀 [BATCH UPLOAD] Iniciando upload em lote:', {
        filesCount: files.length,
        totalSize: `${(files.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)}MB`,
        collection: collection,
        timestamp: new Date().toISOString()
      });
      return documentsApi.uploadBatch(files, collection);
    },
    {
      onSuccess: (data) => {
        console.log('✅ [BATCH UPLOAD] Upload em lote iniciado:', {
          batchId: data.batch_id,
          totalFiles: data.total_files,
          collection: data.collection_name,
          timestamp: new Date().toISOString()
        });
        
        toast({
          title: 'Lote de processamento iniciado',
          description: `${data.total_files} arquivos sendo processados e indexados para busca`,
          status: 'success',
          duration: 5000,
        });

        // Set batch processing state
        setBatchProcessing({
          batch_id: data.batch_id,
          total: data.total_files,
          completed: 0,
          failed: 0,
        });

        setSelectedFiles([]);
        // Mantém a base selecionada para enviar vários documentos seguidos.
        setShowPreview(false);
        setDocumentPreview(null);
        setShowRealResults(false);
        setRealResults(null);

        // Start polling batch status
        console.log('🔄 [BATCH UPLOAD] Iniciando polling para lote:', data.batch_id);
        pollBatchStatus(data.batch_id);
      },
      onError: (error: any) => {
        console.error('❌ [BATCH UPLOAD] Erro no upload em lote:', {
          error: error.response?.data?.detail || error.message,
          status: error.response?.status,
          timestamp: new Date().toISOString()
        });
        
        const detail = error.response?.data?.detail;
        const code = error.response?.status;
        const isLimit = [402, 409, 413, 429].includes(code);
        toast({
          title: isLimit ? 'Limite do seu plano' : 'Não foi possível enviar os documentos',
          description: detail || 'Tente novamente em instantes. Se continuar, fale com o suporte.',
          status: isLimit ? 'warning' : 'error',
          duration: 6000,
          isClosable: true,
        });
      },
    }
  );

  // Poll batch status
  const pollBatchStatus = (batchId: string) => {
    console.log('🔄 [BATCH POLLING] Iniciando polling para lote:', batchId);
    let pollCount = 0;
    
    const interval = setInterval(async () => {
      pollCount++;
      console.log(`🔄 [BATCH POLLING] Tentativa ${pollCount} para lote ${batchId}`);
      
      try {
        const status = await documentsApi.getBatchStatus(batchId);
        console.log('📊 [BATCH POLLING] Status do lote recebido:', {
          batchId: batchId,
          total: status.total_documents,
          completed: status.completed,
          failed: status.failed,
          status: status.status,
          documents: Object.keys(status.documents || {}).length,
          pollCount: pollCount,
          timestamp: new Date().toISOString()
        });
        
        setBatchProcessing({
          batch_id: batchId,
          total: status.total_documents,
          completed: status.completed,
          failed: status.failed,
        });
        
        if (status.status === 'completed') {
          console.log('🎉 [BATCH POLLING] Lote processado:', batchId, status);
          clearInterval(interval);
          setBatchProcessing(null);
          
          // Armazena e exibe os resultados do lote
          setBatchResults(status);
          setShowBatchResults(true);
          
          toast({
            title: 'Lote processado',
            description: `${status.completed} arquivos processados com sucesso${status.failed > 0 ? `, ${status.failed} falharam` : ''}`,
            status: status.failed > 0 ? 'warning' : 'success',
            duration: 7000,
          });
          queryClient.invalidateQueries('collections');
        } else if (status.status === 'failed') {
          console.error('💥 [BATCH POLLING] Lote falhou:', {
            batchId: batchId,
            completed: status.completed,
            failed: status.failed,
            pollCount: pollCount
          });
          clearInterval(interval);
          setBatchProcessing(null);
          toast({
            title: 'Lote falhou',
            description: `${status.completed} arquivos processados, ${status.failed} falharam`,
            status: 'error',
            duration: 7000,
          });
        }
        
        // Timeout após 10 minutos para lotes (300 tentativas * 2 segundos)
        if (pollCount >= 300) {
          console.warn('⏰ [BATCH POLLING] Timeout - parando polling do lote:', batchId);
          clearInterval(interval);
          setBatchProcessing(null);
          toast({
            title: 'Timeout no processamento do lote',
            description: 'O processamento está demorando mais que o esperado. Verifique o status manualmente.',
            status: 'warning',
            duration: 7000,
          });
        }
      } catch (error) {
        console.error('❌ [BATCH POLLING] Erro ao consultar status do lote:', {
          batchId: batchId,
          error: error,
          pollCount: pollCount
        });
        clearInterval(interval);
        setBatchProcessing(null);
        toast({
          title: 'Erro ao consultar status do lote',
          description: 'Não foi possível consultar o status do processamento em lote',
          status: 'error',
          duration: 5000,
        });
      }
    }, 2000);
  };

  // Função para buscar resultados reais do Qdrant/Neo4j
  const fetchRealResults = async (documentId: string) => {
    setIsLoadingRealResults(true);
    try {
      console.log('🔍 [REAL RESULTS] Buscando dados reais do Qdrant para:', documentId);

      // Aguarda um pouco para garantir que os dados foram indexados
      await new Promise(resolve => setTimeout(resolve, 3000));

      const realData = await documentsApi.getProcessingResults(documentId);
      console.log('✅ [REAL RESULTS] Dados reais obtidos:', realData);

      setRealResults(realData);
      setShowRealResults(true);

      // Esconde resultados simples se estava exibindo
      setShowResults(false);

      toast({
        title: 'Dados carregados!',
        description: `${realData.qdrant?.vectors_count || 0} trecho(s) indexado(s) para busca`,
        status: 'success',
        duration: 7000,
      });
      
    } catch (error: any) {
      console.error('❌ [REAL RESULTS] Erro ao buscar dados reais:', error);
      toast({
        title: 'Erro ao carregar dados reais',
        description: 'Mostrando resultados básicos. Os dados podem ainda estar sendo indexados.',
        status: 'warning',
        duration: 5000,
      });
    } finally {
      setIsLoadingRealResults(false);
    }
  };

  // Poll document status
  const pollDocumentStatus = (documentId: string) => {
    console.log('🔄 [POLLING] Iniciando polling para documento:', documentId);
    let pollCount = 0;
    
    const interval = setInterval(async () => {
      pollCount++;
      console.log(`🔄 [POLLING] Tentativa ${pollCount} para documento ${documentId}`);
      
      try {
        const status = await documentsApi.getStatus(documentId);
        console.log('📊 [POLLING] Status recebido:', {
          documentId: documentId,
          status: status.status,
          progress: status.progress,
          message: status.message,
          pollCount: pollCount,
          timestamp: new Date().toISOString()
        });
        
        if (status.status === 'completed') {
          console.log('🎉 [POLLING] Processamento concluído:', documentId, status);
          clearInterval(interval);
          setProcessingDocuments(prev => prev.filter(id => id !== documentId));
          
          // Primeiro armazena e exibe os resultados básicos
          if (status.statistics || status.chunks_count) {
            setProcessingResults({
              documentId: documentId,
              filename: status.filename || 'Documento',
              statistics: status.statistics,
              metadata: status.metadata,
              chunks_count: status.chunks_count,
              message: status.message
            });
            setShowResults(true);
          }
          
          toast({
            title: 'Processamento concluído',
            description: `Documento processado e indexado! ${status.message}`,
            status: 'success',
            duration: 5000,
          });
          
          // Em seguida busca os dados reais do Qdrant/Neo4j
          fetchRealResults(documentId);
          
          queryClient.invalidateQueries('collections');
        } else if (status.status === 'failed') {
          console.error('💥 [POLLING] Processamento falhou:', {
            documentId: documentId,
            message: status.message,
            pollCount: pollCount
          });
          clearInterval(interval);
          setProcessingDocuments(prev => prev.filter(id => id !== documentId));
          toast({
            title: 'Processamento falhou',
            description: status.message || 'Erro no processamento',
            status: 'error',
            duration: 7000,
          });
        } else if (status.status === 'processing') {
          console.log(`⏳ [POLLING] Ainda processando (${Math.round(status.progress * 100)}%):`, status.message);
        }
        
        // Timeout após 5 minutos (150 tentativas * 2 segundos)
        if (pollCount >= 150) {
          console.warn('⏰ [POLLING] Timeout - parando polling:', documentId);
          clearInterval(interval);
          setProcessingDocuments(prev => prev.filter(id => id !== documentId));
          toast({
            title: 'Timeout no processamento',
            description: 'O processamento está demorando mais que o esperado. Verifique o status manualmente.',
            status: 'warning',
            duration: 7000,
          });
        }
      } catch (error) {
        console.error('❌ [POLLING] Erro ao consultar status:', {
          documentId: documentId,
          error: error,
          pollCount: pollCount
        });
        clearInterval(interval);
        setProcessingDocuments(prev => prev.filter(id => id !== documentId));
        toast({
          title: 'Erro ao consultar status',
          description: 'Não foi possível consultar o status do processamento',
          status: 'error',
          duration: 5000,
        });
      }
    }, 2000);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      // Suporta múltiplos arquivos agora
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    }
  }, []);

  // Envia e processa direto (UM clique) — sem o passo técnico de "preview/embedding"
  // intermediário. O cliente escolhe arquivo + base e clica uma vez; o backend extrai.
  const handleConfirmUpload = () => {
    if (selectedFiles.length === 0 || !collectionName.trim()) {
      toast({
        title: 'Faltou algo',
        description: 'Escolha o arquivo e a base de conhecimento.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Lote ou único pela quantidade de arquivos (não depende mais de preview)
    const isBatchPreview = selectedFiles.length > 1;

    if (isBatchPreview) {
      console.log('✅ [CONFIRM BATCH UPLOAD] Confirmando upload de lote:', {
        filesCount: selectedFiles.length,
        collection: collectionName.trim(),
        knowledgeGraph: true,
        timestamp: new Date().toISOString()
      });

      // Para lote, usa apenas arquivos válidos se tiver informação de erro
      const validFiles = selectedFiles;  // Todos os arquivos já foram validados no preview

      batchUploadMutation.mutate({
        files: validFiles,
        collection: collectionName.trim(),
      });
    } else {
      console.log('✅ [CONFIRM UPLOAD] Confirmando upload único:', {
        filename: selectedFiles[0].name,
        collection: collectionName.trim(),
        knowledgeGraph: true,
        timestamp: new Date().toISOString()
      });

      uploadMutation.mutate({
        file: selectedFiles[0],
        collection: collectionName.trim(),
      });
    }
  };

  // Função para cancelar preview (unificada)
  const handleCancelPreview = () => {
    setShowPreview(false);
    setDocumentPreview(null);
    // Mantém os arquivos selecionados e collection para o usuário tentar novamente
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true, // Permite múltiplos arquivos
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/html': ['.html'],
      'text/markdown': ['.md'],
      'text/plain': ['.txt'],
      'application/json': ['.json'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      // Áudios (transcritos automaticamente) — inclui .ogg do WhatsApp.
      'audio/ogg': ['.ogg', '.opus'],
      'application/ogg': ['.ogg'],
      'audio/mpeg': ['.mp3', '.mpga'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/wav': ['.wav'],
      'audio/x-wav': ['.wav'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
      'audio/webm': ['.webm'],
      'video/mp4': ['.mp4'],
    },
  });




  return (
    <Box>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size="lg" mb={2}>Enviar Documentos</Heading>
          <Text color="gray.600">
            Envie arquivos para a sua base e converse com eles no chat
          </Text>
        </Box>

        <Card>
          <CardHeader>
            <Heading size="md">Novo Documento</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* Dropzone */}
              <Box
                {...getRootProps()}
                border="2px dashed"
                borderColor={isDragActive ? "brand.500" : "gray.300"}
                borderRadius="lg"
                p={8}
                textAlign="center"
                cursor="pointer"
                bg={isDragActive ? "brand.50" : "gray.50"}
                _hover={{ borderColor: "brand.400", bg: "brand.50" }}
                transition="all 0.2s"
              >
                <input {...getInputProps()} />
                <VStack spacing={4}>
                  <FiUpload size={48} color={isDragActive ? "#2196f3" : "#718096"} />
                  <Box>
                    <Text fontSize="lg" fontWeight="semibold">
                      {isDragActive
                        ? "Solte o arquivo aqui"
                        : "Arraste um arquivo ou clique para selecionar"}
                    </Text>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Formatos suportados: {supportedFormats?.supported_formats?.join(', ') || 'PDF, DOCX, PPTX, HTML, MD, TXT, JSON, XLSX, CSV'}
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Tamanho máximo: {Math.round((supportedFormats?.max_file_size || 50000000) / 1024 / 1024)}MB
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {/* Selected file */}
              {selectedFiles.length > 0 && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon as={FiFile} />
                  <Box flex="1">
                    <Text fontWeight="semibold">
                      {selectedFiles.length === 1 
                        ? selectedFiles[0].name 
                        : `${selectedFiles.length} arquivos selecionados`}
                    </Text>
                    <Text fontSize="sm">
                      {selectedFiles.length === 1 
                        ? `${(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB`
                        : `Total: ${(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB`}
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedFiles([])}
                    colorScheme="red"
                  >
                    Limpar
                  </Button>
                </Alert>
              )}

              {/* Collection selection */}
              <FormControl>
                <FormLabel>Base de conhecimento</FormLabel>
                <Box position="relative">
                  <Input
                    placeholder="Digite para buscar ou criar (ex: celplan_comercial)"
                    value={collectionName}
                    onChange={(e) => { setCollectionName(e.target.value); setBasePickerOpen(true); }}
                    onFocus={() => setBasePickerOpen(true)}
                    onBlur={() => setTimeout(() => setBasePickerOpen(false), 150)}
                  />
                  {basePickerOpen && (() => {
                    const term = collectionName.trim().toLowerCase();
                    const matches = collections.filter((c) => c.name.toLowerCase().includes(term));
                    const exact = collections.some((c) => c.name.toLowerCase() === term);
                    if (matches.length === 0 && !term) return null;
                    return (
                      <Box position="absolute" zIndex={20} mt={1} w="100%" bg="white" borderWidth="1px"
                           borderRadius="md" shadow="lg" maxH="280px" overflowY="auto">
                        {matches.map((c) => (
                          <HStack key={c.name} px={3} py={2} cursor="pointer" _hover={{ bg: 'gray.100' }}
                            onMouseDown={() => { setCollectionName(c.name); setBasePickerOpen(false); }}>
                            <FiDatabase color="#718096" />
                            <Text fontSize="sm" flex={1} noOfLines={1}>{c.name}</Text>
                            <Text fontSize="xs" color="gray.400">{c.vectors_count} trechos</Text>
                          </HStack>
                        ))}
                        {matches.length === 0 && (
                          <Text px={3} py={2} fontSize="xs" color="gray.500">Nenhuma base encontrada.</Text>
                        )}
                        {term && !exact && (
                          <HStack px={3} py={2} cursor="pointer" color="brand.600" borderTopWidth={matches.length ? '1px' : 0}
                            _hover={{ bg: 'brand.50' }} onMouseDown={() => setBasePickerOpen(false)}>
                            <FiPlus />
                            <Text fontSize="sm">Criar nova base: “{collectionName.trim()}”</Text>
                          </HStack>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
                <FormHelperText>
                  Digite para filtrar suas bases (busca por qualquer parte do nome). Se não existir, ela é criada ao enviar.
                </FormHelperText>
              </FormControl>


              {/* Botão único — UM clique envia e processa (sem passo técnico de preview) */}
              <Button
                colorScheme="blue"
                size="lg"
                leftIcon={<FiUpload />}
                onClick={handleConfirmUpload}
                isLoading={uploadMutation.isLoading || batchUploadMutation.isLoading}
                loadingText={
                  selectedFiles.length > 1
                    ? `Processando ${selectedFiles.length} arquivos...`
                    : "Processando documento..."
                }
                isDisabled={selectedFiles.length === 0 || !collectionName.trim() || uploadMutation.isLoading || batchUploadMutation.isLoading}
                width="100%"
                variant="solid"
                h="60px"
              >
                {selectedFiles.length > 1
                  ? `Enviar e processar ${selectedFiles.length} arquivos`
                  : 'Enviar e processar'}
              </Button>

              <Alert status="info" borderRadius="md" mt={2}>
                <AlertIcon />
                <Box fontSize="sm">
                  <Text>O texto é extraído e indexado automaticamente. Você acompanha o progresso aqui e, ao terminar, já pode perguntar sobre o conteúdo no Assistente.</Text>
                </Box>
              </Alert>
            </VStack>
          </CardBody>
        </Card>

        {/* Document Preview (unificado para arquivo único e lote) */}
        {showPreview && documentPreview && (
          <DocumentPreview
            preview={documentPreview}
            onContinue={handleConfirmUpload}
            onCancel={handleCancelPreview}
            isProcessing={uploadMutation.isLoading || batchUploadMutation.isLoading}
          />
        )}

        {/* Processing Results */}
        {showResults && processingResults && (() => {
          const _chunks = Number(processingResults.statistics?.chunks_count ?? processingResults.chunks_count ?? 0) || 0;
          const _ok = _chunks > 0;
          return (
          <Card borderColor={_ok ? 'green.500' : 'orange.400'} borderWidth={2}>
            <CardHeader bg={_ok ? 'green.50' : 'orange.50'}>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={_ok ? FiCheckCircle : FiXCircle} color={_ok ? 'green.500' : 'orange.500'} boxSize={6} />
                  <Heading size="md" color={_ok ? 'green.700' : 'orange.700'}>
                    {_ok ? 'Processamento Concluído!' : 'Nenhum texto extraído — não indexado'}
                  </Heading>
                </HStack>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setShowResults(false);
                    setProcessingResults(null);
                  }}
                >
                  Fechar
                </Button>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" fontSize="lg">{processingResults.filename}</Text>
                  <Text color="gray.600" fontSize="sm">{processingResults.message}</Text>
                </Box>
                
                {processingResults.statistics && (
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat>
                      <StatLabel>Chunks</StatLabel>
                      <StatNumber>{processingResults.statistics.chunks_count || processingResults.chunks_count || 0}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Páginas</StatLabel>
                      <StatNumber>{processingResults.statistics.pages || 0}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Caracteres</StatLabel>
                      <StatNumber>{(processingResults.statistics.text_length || 0).toLocaleString()}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Tamanho</StatLabel>
                      <StatNumber>{processingResults.statistics.file_size_mb || 0} MB</StatNumber>
                    </Stat>
                  </SimpleGrid>
                )}
                
                <Divider />
                
                {isLoadingRealResults ? (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Carregando dados do processamento...</Text>
                      <Text fontSize="sm" mt={1}>
                        Conferindo os trechos indexados para busca.
                        Isso pode levar alguns segundos...
                      </Text>
                      <Progress 
                        size="sm" 
                        isIndeterminate 
                        colorScheme="blue" 
                        mt={2}
                        borderRadius="md"
                      />
                    </Box>
                  </Alert>
                ) : (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Documento pronto para consultas!</Text>
                      <Text fontSize="sm" mt={1}>
                        O documento foi processado e indexado com sucesso. Você já pode fazer perguntas
                        sobre ele na aba de Chat.
                      </Text>
                    </Box>
                  </Alert>
                )}
                
                <HStack justify="flex-end" spacing={3}>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowResults(false);
                      setProcessingResults(null);
                    }}
                  >
                    Fazer Novo Upload
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    leftIcon={<Icon as={FiMessageSquare} />}
                    as={Link}
                    to="/chat"
                  >
                    Ir para o Chat
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
          );
        })()}

        {/* Real Processing Results */}
        {showRealResults && realResults && (
          <PostProcessingResults
            results={realResults}
            onClose={() => {
              setShowRealResults(false);
              setRealResults(null);
            }}
          />
        )}

        {/* Batch Results */}
        {showBatchResults && batchResults && (
          <Card borderColor="green.500" borderWidth={2}>
            <CardHeader bg="green.50">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiCheckCircle} color="green.500" boxSize={6} />
                  <Heading size="md" color="green.700">Lote Processado com Sucesso!</Heading>
                </HStack>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setShowBatchResults(false);
                    setBatchResults(null);
                  }}
                >
                  Fechar
                </Button>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Summary */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg">
                    {batchResults.completed} de {batchResults.total_documents} arquivos processados
                  </Text>
                  <Text color="gray.600" fontSize="sm">
                    {batchResults.failed > 0 && `${batchResults.failed} falharam • `}
                    Indexados e prontos para busca
                  </Text>
                </Box>

                {/* Aggregate Statistics */}
                {batchResults.documents && (
                  <Box>
                    <Text fontWeight="semibold" mb={3}>Estatísticas Agregadas</Text>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Total de Chunks</StatLabel>
                        <StatNumber>
                          {Object.values(batchResults.documents)
                            .reduce((sum, doc) => sum + (doc.chunks_count || 0), 0)
                            .toLocaleString()}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Total de Páginas</StatLabel>
                        <StatNumber>
                          {Object.values(batchResults.documents)
                            .reduce((sum, doc) => sum + (doc.statistics?.pages || 0), 0)}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Total de Caracteres</StatLabel>
                        <StatNumber>
                          {Object.values(batchResults.documents)
                            .reduce((sum, doc) => sum + (doc.statistics?.text_length || 0), 0)
                            .toLocaleString()}
                        </StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Tamanho Total</StatLabel>
                        <StatNumber>
                          {Object.values(batchResults.documents)
                            .reduce((sum, doc) => sum + (doc.statistics?.file_size_mb || 0), 0)
                            .toFixed(2)} MB
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                )}

                <Divider />

                {/* Individual Files */}
                <Box>
                  <Text fontWeight="semibold" mb={3}>Arquivos Processados</Text>
                  <VStack spacing={3} align="stretch">
                    {batchResults.documents && Object.entries(batchResults.documents).map(([docId, doc]) => (
                      <Box key={docId} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="medium" fontSize="sm">
                            {doc.filename || `Documento ${docId.slice(0, 8)}`}
                          </Text>
                          <HStack spacing={2}>
                            {doc.status === 'completed' && <Icon as={FiCheckCircle} color="green.500" />}
                            {doc.status === 'failed' && <Icon as={FiXCircle} color="red.500" />}
                          </HStack>
                        </HStack>
                        
                        {doc.statistics && (
                          <HStack spacing={4} fontSize="xs" color="gray.600">
                            <Text>{doc.statistics.chunks_count || doc.chunks_count || 0} chunks</Text>
                            <Text>{doc.statistics.pages || 0} páginas</Text>
                            <Text>{(doc.statistics.text_length || 0).toLocaleString()} caracteres</Text>
                            <Text>{doc.statistics.file_size_mb || 0} MB</Text>
                          </HStack>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>

                <Divider />
                
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Todos os documentos estão prontos para consultas!</Text>
                    <Text fontSize="sm" mt={1}>
                      Os {batchResults.completed} documentos foram processados e indexados com sucesso.
                      Você já pode fazer perguntas sobre eles na aba de Chat.
                    </Text>
                  </Box>
                </Alert>
                
                <HStack justify="flex-end" spacing={3}>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowBatchResults(false);
                      setBatchResults(null);
                    }}
                  >
                    Fazer Novo Upload
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    leftIcon={<Icon as={FiMessageSquare} />}
                    as={Link}
                    to="/chat"
                  >
                    Ir para o Chat
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Batch processing status */}
        {batchProcessing && (
          <Card>
            <CardHeader>
              <Heading size="md">Processamento em Lote</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="semibold">Lote: {batchProcessing.batch_id.slice(0, 8)}...</Text>
                  <Text fontSize="sm" color="gray.600">
                    {batchProcessing.completed + batchProcessing.failed} / {batchProcessing.total} arquivos
                  </Text>
                </HStack>
                
                <Progress
                  value={((batchProcessing.completed + batchProcessing.failed) / batchProcessing.total) * 100}
                  colorScheme={batchProcessing.failed > 0 ? "orange" : "blue"}
                  size="lg"
                  borderRadius="md"
                />
                
                <HStack justify="space-between" fontSize="sm">
                  <Text color="green.600">✅ Concluídos: {batchProcessing.completed}</Text>
                  <Text color="red.600">❌ Falharam: {batchProcessing.failed}</Text>
                  <Text color="blue.600">⏳ Restantes: {batchProcessing.total - batchProcessing.completed - batchProcessing.failed}</Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Processing status */}
        {processingDocuments.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Documentos em Processamento</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {processingDocuments.map((docId) => (
                  <ProcessingIndicator key={docId} documentId={docId} />
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Collections summary */}
        {collections.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Bases existentes</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                {collections.map((collection) => (
                  <HStack key={collection.name} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold">{collection.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {collection.vectors_count} vetores
                      </Text>
                    </VStack>
                    <Button size="sm" variant="outline" colorScheme="brand">
                      Ver detalhes
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

      </VStack>
    </Box>
  );
};

// Component for showing processing status with timer
const ProcessingIndicator = ({ documentId }: { documentId: string }) => {
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const { data: status } = useQuery(
    ['document-status', documentId],
    () => documentsApi.getStatus(documentId),
    {
      refetchInterval: 2000,
      enabled: true,
    }
  );

  // Update elapsed time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const formatElapsedTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const estimateRemainingTime = (progress: number, elapsed: number) => {
    if (progress <= 0.05) return "Calculating...";
    
    const totalEstimated = elapsed / progress;
    const remaining = totalEstimated - elapsed;
    
    if (remaining <= 0) return "Almost done...";
    
    const remainingSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    
    if (minutes > 0) {
      return `~${minutes}m ${seconds}s remaining`;
    }
    return `~${seconds}s remaining`;
  };

  if (!status) return null;

  const getStatusIcon = () => {
    switch (status.status) {
      case 'completed':
        return <FiCheckCircle color="green" />;
      case 'failed':
        return <FiXCircle color="red" />;
      default:
        return <FiFile color="blue" />;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'blue';
    }
  };

  return (
    <Box p={4} border="1px" borderColor="gray.200" borderRadius="md" bg={status.status === 'completed' ? 'green.50' : status.status === 'failed' ? 'red.50' : 'blue.50'}>
      <HStack justify="space-between" mb={2}>
        <HStack>
          {getStatusIcon()}
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold">Documento {documentId.slice(0, 8)}...</Text>
            {status.status === 'processing' && (
              <HStack spacing={2}>
                <Text fontSize="xs" color="blue.600">
                  Processando há {formatElapsedTime(elapsedTime)}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  • {estimateRemainingTime(status.progress, elapsedTime)}
                </Text>
              </HStack>
            )}
            {status.status === 'completed' && (
              <Text fontSize="xs" color="green.600">
                ✅ Concluído em {formatElapsedTime(elapsedTime)} - Pronto para consultas
              </Text>
            )}
            {status.status === 'failed' && (
              <Text fontSize="xs" color="red.600">
                ❌ Erro após {formatElapsedTime(elapsedTime)} de processamento
              </Text>
            )}
          </VStack>
        </HStack>
        <VStack align="end" spacing={0}>
          <Text fontSize="lg" color="gray.700" fontWeight="bold">
            {status.status === 'processing' ? `${Math.round(status.progress * 100)}%` : status.status}
          </Text>
          {status.status === 'processing' && (
            <Text fontSize="xs" color="gray.500">
              {Math.round(status.progress * 100)}% concluído
            </Text>
          )}
        </VStack>
      </HStack>
      
      <Progress
        value={status.progress * 100}
        colorScheme={getStatusColor()}
        size="md"
        mb={3}
        borderRadius="md"
        bg="gray.200"
      />
      
      {status.message && (
        <Alert status={status.status === 'failed' ? 'error' : 'info'} size="sm" borderRadius="md" mb={2}>
          <AlertIcon boxSize="12px" />
          <Text fontSize="sm">
            {status.message}
          </Text>
        </Alert>
      )}

      {/* Progress details for active processing */}
      {status.status === 'processing' && (
        <HStack justify="space-between" fontSize="xs" color="gray.600">
          <Text>Etapa atual: {Math.round(status.progress * 100)}%</Text>
          <Text>Tempo decorrido: {formatElapsedTime(elapsedTime)}</Text>
        </HStack>
      )}
    </Box>
  );
};

export default DocumentUpload;